/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import React, { useContext, type ReactElement, useMemo, useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
} from 'material-react-table';
import { Stack, CircularProgress, Button } from '@mui/material';
import { TravelDetailsModel } from './TravelDetailsModel';
import axios from 'axios';
import { ConvertDateUtil } from '../utils/ConvertDateUtil';
import { GetStageUtil } from '../utils/GetStageUtil';
import { TravelContext } from '../store/TravelContext';
import { useMsal } from '@azure/msal-react';
import { utils } from 'xlsx';
import { InteractionRequiredAuthError } from '@azure/msal-common';
import { Toast } from './Toast';
import { FormatPreTravelRecords } from '../utils/FormatRecordsUtil';

export type Travel = {
  id: string;
  traveler_name: string;
  email_address: string;
  origin_country: string;
  destination_country: string;
  travel_start_date: string;
  travel_end_date: string;
  stage: string;
  ritm_number: any;
  extra_details: any;
};

export const TravelHistory = (props: any): ReactElement<React.FC> => {
  const tableInstanceRef: any = useRef(null);
  let context;
  const [open, setOpen] = React.useState(false);
  const { instance, accounts } = useMsal();
  const [travelData, setTravelData] = React.useState({
    traveler_name: '',
    email_address: '',
    origin_country: '',
    destination_country: '',
    travel_start_date: '',
    travel_end_date: '',
    stage: '',
    extra_details: {},
    panel1: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<Travel[]>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [saveDisable, setSaveDisable] = useState(true);
  const [toastState, setToastState] = useState({
    open: false,
    type: 'success',
    message: '',
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const travelCtx = useContext(TravelContext);
  const getAccessToken = async (): Promise<
    { accessToken: string; idToken: string } | undefined
  > => {
    if (accounts.length > 0) {
      const request = {
        scopes: ['User.Read'],
        account: accounts[0],
      };
      try {
        const response = await instance.acquireTokenSilent(request);
        return { accessToken: response.accessToken, idToken: response.idToken };
      } catch (error) {
        // acquireTokenSilent can fail for a number of reasons, fallback to interaction
        if (error instanceof InteractionRequiredAuthError) {
          const response = await instance.acquireTokenPopup(request);
          return { accessToken: response.accessToken, idToken: response.idToken };
        }
      }
    }
  };
  const getTravelRecords = async (): Promise<void> => {
    try {
      const token = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL as string;
      const response: any = await axios
        .get(`${apiUrl}/travelDetails/id`, {
          headers: {
            Authorization: `Bearer ${token?.idToken}`,
            graph_token: token?.accessToken,
            'Content-Type': 'application/json',
          },
        })
        .then((responseBody) => responseBody);
      if (response.data.status !== 200) {
        throw Error(response.data.msg);
      }
      const data = [];
      for (let i = 0; i < response.data.msg.length; i++) {
        const stageDetails = GetStageUtil(
          response.data.msg[i].travel_start_date,
          response.data.msg[i].travel_end_date
        );
        const travelData = {
          ...response.data.msg[i],
          stage: stageDetails.name,
          stageValue: stageDetails.stage,
        };
        travelData.stage = travelData.extra_details?.is_cancelled ? 'Cancelled' : travelData.stage;
        data.push(travelData);
      }
      travelCtx.setPreCount(data.length);
      data.sort((a: any, b: any) => a.stageValue - b.stageValue);
      const checkedTravel = data
        .filter((travel) => travel.extra_details?.isNotificationDisabled === true)
        .map((data) => data.id)
        .reduce((acc, elem) => {
          acc[elem] = true;
          return acc;
        }, {});
      setRows(data);
      setRowSelection((prev) => ({ ...prev, ...checkedTravel }));
      setIsLoading(false);
    } catch (err) {
      console.log('Error', err);
      setIsLoading(false);
    }
  };
  const getCheckedUnchecked = () => {
    const rowSelectedTravelId = Object.keys(rowSelection);
    const uncheckedTravelIds: number[] = [];
    rows.map((travel) => {
      if (travel.extra_details?.isNotificationDisabled === true) {
        if (!rowSelectedTravelId.includes('' + travel.id)) {
          uncheckedTravelIds.push(Number(travel.id));
        }
      }
    });
    return { rowSelectedTravelId, uncheckedTravelIds };
  };
  useEffect((): void => {
    (async (): Promise<void> => {
      await getTravelRecords();
    })();
  }, []);
  useEffect((): void => {
    (async (): Promise<void> => {
      if (props.activepanel.active === 'panel1') {
        if (props.activepanel?.export) {
          handleDownload();
        }
        if (props.activepanel?.supress) {
          if (!saveDisable) await handleSave();
          else {
            setToastState({
              open: true,
              message: 'No checkbox checked',
              type: 'error',
            });
          }
        }
      }
    })();
  }, [props.activepanel]);
  useEffect(() => {
    const result = getCheckedUnchecked();
    const check =
      result.rowSelectedTravelId.length > 0 || result.uncheckedTravelIds.length > 0 ? false : true;
    setSaveDisable(check);
  }, [rowSelection]);
  /* istanbul ignore next */
  const handleOnclick = (data: Travel) => {
    const record = {
      traveler_name: data.traveler_name,
      email_address: data.email_address,
      origin_country: data.origin_country,
      destination_country: data.destination_country,
      travel_start_date: data.travel_start_date,
      travel_end_date: data.travel_end_date,
      stage: data.stage,
      extra_details: data.extra_details,
      panel1: true,
    };
    setTravelData({ ...record });
    setOpen(true);
  };
  /* istanbul ignore next */
  const handleDownload = () => {
    let tableRows: any = [];
    if (
      tableInstanceRef?.current &&
      typeof tableInstanceRef?.current?.getFilteredRowModel === 'function'
    ) {
      tableInstanceRef.current.getFilteredRowModel().flatRows.forEach((element: any) => {
        tableRows.push(element.original);
      });
    } else {
      tableRows = rows;
    }
    const csvData = FormatPreTravelRecords(tableRows);
    const data = csvData.map((row) => {
      return {
        'Traveler Name': row.traveler_name,
        Email: row.email_address,
        'Origin Place': row.origin_country,
        Destination: row.destination_country,
        'Travel Start Date': ConvertDateUtil(row.travel_start_date),
        'Travel End Date': ConvertDateUtil(row.travel_end_date),
      };
    });
    const ws = utils.json_to_sheet(data);
    const csv = utils.sheet_to_csv(ws);

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    // Create a download link and click it
    const link = document.createElement('a');
    const downloadUrl = URL.createObjectURL(blob);
    link.href = downloadUrl;
    link.download = 'pre_travel_records.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  /* istanbul ignore next */
  const handleSave = async () => {
    try {
      const result = getCheckedUnchecked();
      const token = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL as string;
      const config = {
        method: 'post',
        url: `${apiUrl}/sa/setNotificationPreferences`,
        headers: {
          Authorization: `Bearer ${token?.idToken}`,
          'Content-Type': 'application/json',
          graph_token: token?.accessToken,
        },
        data: {
          idsToEnableNotification: result.uncheckedTravelIds,
          idsToDisableNotification: result.rowSelectedTravelId.map(Number),
        },
      };
      const response = await axios(config);
      if (response.data.status !== 200) {
        throw Error(response.data.msg);
      }
      await getTravelRecords();
      setToastState({
        open: true,
        message: 'Suppress Notification and Table Updated Successfully',
        type: 'success',
      });
    } catch (error) {
      setToastState({
        open: true,
        message: 'Failed to Update Suppress Notification',
        type: 'error',
      });
      console.log('Error while saving suppress notification', error);
    }
  };
  useEffect(() => {
    let tableRows: any = [];
    if (
      tableInstanceRef?.current &&
      typeof tableInstanceRef?.current?.getFilteredRowModel === 'function'
    ) {
      tableInstanceRef.current.getFilteredRowModel().flatRows.forEach((element: any) => {
        tableRows.push(element.original);
      });
    } else {
      tableRows = rows;
    }
    travelCtx.setPreCount(tableRows.length);
  }, [columnFilters]);
  useEffect(() => {
    let tableRows: any = [];
    if (
      tableInstanceRef?.current &&
      typeof tableInstanceRef?.current?.getFilteredRowModel === 'function'
    ) {
      tableInstanceRef.current.getFilteredRowModel().flatRows.forEach((element: any) => {
        tableRows.push(element.original);
      });
    } else {
      tableRows = rows;
    }
    travelCtx.setPreCount(tableRows.length);
  }, [globalFilter]);

  const columns = useMemo<MRT_ColumnDef<Travel>[]>(
    () => [
      {
        accessorKey: 'traveler_name',
        header: 'Name',
      },
      {
        accessorKey: 'email_address',
        header: 'Email Id',
      },
      {
        accessorKey: 'origin_country',
        header: 'Origin Place',
      },
      {
        accessorKey: 'destination_country',
        header: 'Destination',
        size: 10,
      },
      {
        accessorFn: (originalRow) => ConvertDateUtil(originalRow.travel_start_date),
        accessorKey: 'travel_start_date',
        header: 'Travel Start Date',
        size: 10,
        Cell: ({ row }) => ConvertDateUtil(row.original.travel_start_date),
      },
      {
        accessorFn: (originalRow) => ConvertDateUtil(originalRow.travel_end_date),
        accessorKey: 'travel_end_date',
        header: 'Travel End Date',
        size: 10,
        Cell: ({ row }) => ConvertDateUtil(row.original.travel_end_date),
      },
      {
        accessorKey: 'stage',
        header: 'Status',
        filterVariant: 'select',
        filterSelectOptions: ['Pre Travel', 'During Travel', 'Post Travel', 'Cancelled'],
      },
      {
        accessor: 'action',
        header: 'Action',
        size: 10,
        Cell: ({ row }) => (
          <Button
            color='inherit'
            variant='outlined'
            sx={{
              fontSize: { xs: '0.5rem', sm: '0.75rem' },
              color: '#F69A19',
            }}
            onClick={() => handleOnclick(row.original)}
          >
            Details
          </Button>
        ),
      },
    ],
    []
  );
  if (isLoading) {
    context = (
      <Grid
        data-testid='Spinner'
        container
        direction='column'
        justifyContent='center'
        alignItems='center'
        height='50vh'
      >
        <CircularProgress size='6rem' />
      </Grid>
    );
  } else {
    context = (
      <Grid container height='100%' justifyContent='flex-start' sx={{ p: '5px' }}>
        <Grid width='100%'>
          <Stack spacing={1}>
            <Stack>
              <MaterialReactTable
                tableInstanceRef={tableInstanceRef}
                muiTablePaperProps={{
                  sx: {
                    px: '1rem',
                    boxShadow: '0',
                  },
                }}
                defaultColumn={{
                  size: 150, //default size is usually 180
                }}
                enableSelectAll={false}
                getRowId={(row) => row.id}
                // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
                enableRowSelection={(row) => travelCtx.isSuperAdmin}
                onRowSelectionChange={setRowSelection}
                state={{ rowSelection, columnFilters, globalFilter }}
                columns={columns}
                onColumnFiltersChange={setColumnFilters}
                onGlobalFilterChange={setGlobalFilter}
                enablePinning
                initialState={{ columnPinning: { right: ['Action'] } }}
                data={rows}
                enableColumnActions={false}
                enableTopToolbar
                muiTableBodyRowProps={{ hover: false }}
                globalFilterModeOptions={['fuzzy', 'contains']}
                displayColumnDefOptions={{
                  'mrt-row-select': {
                    header: 'Suppress',
                  },
                }}
                positionToolbarAlertBanner='none'
                muiTableHeadCellProps={{
                  sx: {
                    bgcolor: '#f1f1f1',
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    background: 'white',
                  },
                }}
              />
            </Stack>
          </Stack>
          {open && (
            <TravelDetailsModel
              travelDetails={travelData}
              open={open}
              onClose={() => setOpen(false)}
            />
          )}
        </Grid>
        {toastState.open && (
          <Toast
            open={toastState.open}
            handleClose={() => setToastState({ ...toastState, open: false })}
            message={toastState.message}
            type={toastState.type}
          />
        )}
      </Grid>
    );
  }
  return context;
};
